import { memo } from 'react';
import { TrendArrowDown } from 'assets/svg/TrendArrowDown';
import TrendArrowUp from 'assets/svg/TrendArrowUp';
import { colorConstants } from 'config/colorConstants';
import { formatNumWithKMB } from 'utils/helperMethods';

type WhitespaceKpi = {
    kpi_id?: string;
    value?: string | number | boolean;
    growth_percentage?: number | null;
    valuePrefix?: string;
    valueSuffix?: string;
    display_label?: string;
};

type WhitespaceHierarchyItem = {
    attribute_type?: string;
    description?: string;
    ex_id?: string;
};

export type WhitespaceTooltipItem = {
    ex_id?: string;
    description?: string;
    is_own_attribute?: boolean;
    hierarchy?: WhitespaceHierarchyItem[];
    kpis?: WhitespaceKpi[];
};

const KPI_IDS = {
    totalValue: ['activate_value_sales', 'value_sales'],
    totalVolume: ['volume_sales', 'activate_volume_sales', 'total_eq_volume'],
};

const TOOLTIP_SHADOW =
    'shadow-[0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_9px_28px_8px_rgba(0,0,0,0.05),0px_6px_16px_rgba(0,0,0,0.08)]';

type TooltipRow = {
    key: string;
    label: string;
    value: string;
    growthPercentage?: number | null;
    emphasizeNumericValue?: boolean;
};

const getKpiByIds = (kpis: WhitespaceKpi[] = [], ids: string[]) =>
    kpis.find((kpi) => ids.includes((kpi.kpi_id ?? '').trim().toLowerCase()));

const formatKpiValue = (kpi?: WhitespaceKpi, options?: { compactNumericValue?: boolean; decimalDigits?: number }) => {
    if (!kpi) return '-';
    const { compactNumericValue = false, decimalDigits = 1 } = options ?? {};
    const rawValue = kpi.value ?? '-';

    if (!compactNumericValue) {
        return `${kpi.valuePrefix ?? ''}${rawValue}${kpi.valueSuffix ?? ''}`;
    }

    const numericValue =
        typeof rawValue === 'number'
            ? rawValue
            : typeof rawValue === 'string'
              ? Number(rawValue.replace(/,/g, '').trim())
              : Number.NaN;

    const valueToDisplay = Number.isFinite(numericValue)
        ? formatNumWithKMB(numericValue, decimalDigits).toUpperCase()
        : rawValue;
    return `${kpi.valuePrefix ?? ''}${valueToDisplay}${kpi.valueSuffix ?? ''}`;
};

const toTitleCase = (value?: string) =>
    String(value ?? '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());

const formatGrowthPercentage = (growthPercentage: number) => {
    const formattedValue = formatNumWithKMB(Math.abs(growthPercentage), 1);
    return growthPercentage < 0 ? `-${formattedValue}%` : `${formattedValue}%`;
};

const TooltipDivider = () => <div className="h-px w-full shrink-0 bg-[#d4d4d8]" />;

const GrowthIndicator = ({ growthPercentage }: { growthPercentage?: number | null }) => {
    if (typeof growthPercentage !== 'number' || !Number.isFinite(growthPercentage) || growthPercentage === 0) {
        return null;
    }

    const isDown = growthPercentage < 0;
    const trendColor = isDown ? colorConstants.growthDown : colorConstants.growthUp;

    return (
        <span
            className="inline-flex shrink-0 items-center gap-0.5 text-[14px] font-medium leading-[22px]"
            style={{ color: trendColor }}
        >
            {isDown ? (
                <TrendArrowDown className="size-[14px] shrink-0" color={trendColor} />
            ) : (
                <TrendArrowUp className="size-[14px] shrink-0" color={trendColor} />
            )}
            <span>{formatGrowthPercentage(growthPercentage)}</span>
        </span>
    );
};

const TooltipDataRow = ({
    label,
    value,
    growthPercentage,
    emphasizeNumericValue = false,
}: Omit<TooltipRow, 'key'>) => (
    <div
        className={`flex w-full gap-6 ${
            emphasizeNumericValue ? 'items-center' : 'items-start'
        }`}
    >
        <p className="shrink-0 text-[14px] font-normal leading-normal text-toolbarLabelGrey">
            {label}
        </p>
        <div
            className={`flex min-w-0 flex-1 justify-end ${
                emphasizeNumericValue ? 'items-center gap-2' : 'items-start'
            }`}
        >
            <p
                className={`min-w-0 text-[14px] leading-[22px] text-blackTitle ${
                    emphasizeNumericValue
                        ? 'text-right font-medium whitespace-nowrap'
                        : 'break-words text-right font-normal'
                }`}
                title={!emphasizeNumericValue ? value : undefined}
            >
                {value}
            </p>
            {emphasizeNumericValue ? <GrowthIndicator growthPercentage={growthPercentage} /> : null}
        </div>
    </div>
);

const buildKpiRows = (
    kpis: WhitespaceKpi[] | undefined,
    selectedYAxisKpiId?: string,
    yAxisAttribute?: { label?: string; value?: string },
): TooltipRow[] => {
    const rows: TooltipRow[] = [];
    const totalValueKpi = getKpiByIds(kpis, KPI_IDS.totalValue);
    const totalVolumeKpi = getKpiByIds(kpis, KPI_IDS.totalVolume);

    if (totalValueKpi) {
        rows.push({
            key: 'total-value',
            label: totalValueKpi.display_label ??'Value Sales',
            value: formatKpiValue(totalValueKpi),
            growthPercentage: totalValueKpi.growth_percentage,
            emphasizeNumericValue: true,
        });
    }

    // When the Y-axis is an attribute bucket (e.g. Size/Pack Range) rather
    // than a KPI, show which bucket this row falls into instead of the
    // fallback price KPI used internally for cell coloring/sorting - that
    // KPI isn't what the Y-axis actually represents in this mode.
    if (yAxisAttribute?.label) {
        rows.push({
            key: 'y-axis-attribute',
            label: yAxisAttribute.label,
            value: yAxisAttribute.value ?? '-',
            emphasizeNumericValue: true,
        });
    } else {
        const avgPriceKpi = kpis?.find(
            (kpi) => (kpi.kpi_id ?? '').trim().toLowerCase() === (selectedYAxisKpiId ?? '').trim().toLowerCase(),
        );
        if (avgPriceKpi) {
            rows.push({
                key: 'avg-price',
                label: avgPriceKpi.display_label ?? 'Avg. Price/Unit',
                value: formatKpiValue(avgPriceKpi),
                emphasizeNumericValue: true,
            });
        }
    }

    if (totalVolumeKpi) {
        rows.push({
            key: 'total-volume',
            label: totalVolumeKpi.display_label ?? 'Total Volume',
            value: formatKpiValue(totalVolumeKpi, { compactNumericValue: true }),
            growthPercentage: totalVolumeKpi.growth_percentage,
            emphasizeNumericValue: true,
        });
    }

    return rows;
};

const WhitespaceItemTooltip = memo(
    ({
        item,
        selectedYAxisKpiId,
        yAxisAttributeLabel,
        yAxisAttributeValue,
    }: {
        item: WhitespaceTooltipItem;
        selectedYAxisKpiId?: string;
        yAxisAttributeLabel?: string;
        yAxisAttributeValue?: string;
    }) => {
        const kpiRows = buildKpiRows(item.kpis, selectedYAxisKpiId, {
            label: yAxisAttributeLabel,
            value: yAxisAttributeValue,
        });
        const hierarchyRows = item.hierarchy ?? [];

        return (
            <div
                className={`flex w-[300px] flex-col items-start rounded-xl bg-white p-[20px] ${TOOLTIP_SHADOW}`}
            >
                <div className="flex w-full flex-col items-start gap-4">
                    <p className="w-full break-words text-[16px] font-semibold leading-normal text-blackTitle">
                        {item.description ?? item.ex_id ?? '-'}
                    </p>

                    {kpiRows.length > 0 ? (
                        <>
                            <TooltipDivider />
                            <div className="flex w-full flex-col gap-2">
                                {kpiRows.map((row) => (
                                    <TooltipDataRow
                                        key={row.key}
                                        label={row.label}
                                        value={row.value}
                                        growthPercentage={row.growthPercentage}
                                        emphasizeNumericValue={row.emphasizeNumericValue}
                                    />
                                ))}
                            </div>
                        </>
                    ) : null}

                    {hierarchyRows.length > 0 ? (
                        <>
                            <TooltipDivider />
                            <div className="flex w-full flex-col gap-2">
                                {hierarchyRows.map((row, index) => (
                                    <TooltipDataRow
                                        key={`${row.attribute_type ?? 'hierarchy'}-${index}`}
                                        label={toTitleCase(row.attribute_type)}
                                        value={row.description ?? row.ex_id ?? '-'}
                                    />
                                ))}
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
        );
    },
);

export default WhitespaceItemTooltip;
